"use client";

import { m } from "motion/react";
import { useMemo } from "react";
import { useInView } from "react-intersection-observer";

interface FloatingFireParticlesProps {
  burnIntensity: number;
  burnStreak: number;
  maxParticles?: number;
  baseParticles?: number;
}

// AFTER HOURS: bright fire embers replaced with a sparse cobalt-tinted
// moondust drift. The burn theme stays inside the canvas/scene, but the
// chrome layer no longer carries orange motion. Density floored to ~3,
// capped well below the original max. Opacity stays whisper-low so the
// particles read as residue, not life.
export function FloatingFireParticles({
  burnIntensity,
  burnStreak,
  maxParticles = 8,
  baseParticles = 3,
}: FloatingFireParticlesProps) {
  const { ref, inView } = useInView({
    rootMargin: "120px 0px",
    triggerOnce: false,
  });
  const particleCount = Math.min(
    baseParticles + Math.floor(burnIntensity * 0.4) + (burnStreak > 1 ? burnStreak - 1 : 0),
    maxParticles,
  );
  const particles = useMemo(
    () =>
      Array.from({ length: particleCount }, (_, i) => ({
        id: `particle-${i}-${burnStreak}`,
        index: i,
        size: 2 + (i % 2),
        left: `${10 + ((i * 11) % 80)}%`,
      })),
    [burnStreak, particleCount],
  );

  return (
    <div ref={ref} className="pointer-events-none absolute inset-0 overflow-hidden">
      {inView &&
        particles.map(({ id, index: i, left, size }) => {
          return (
            <m.div
              key={id}
              className="absolute rounded-full"
              animate={{
                y: [0, -420],
                opacity: [0, 0.18, 0.12, 0],
              }}
              transition={{
                duration: 9 + (i % 3) * 2,
                delay: (i * 0.7) % 6,
                ease: "linear",
                repeat: Infinity,
              }}
              style={{
                left,
                bottom: "-12px",
                width: `${size}px`,
                height: `${size}px`,
                background: "rgba(155, 176, 240, 0.55)",
                boxShadow: "0 0 1px rgba(94, 123, 250, 0.3)",
              }}
            />
          );
        })}
    </div>
  );
}
